/**
 * @fileOverview React component for Turnstile integration.
 * @module Turnstile
 */

import React, { useEffect, useState, useRef } from "react";

/**
 * @typedef { "ar-eg" | "ar" | "de" | "en" | "es" | "fa" | "fr" | "id" | "it" | "ja" | "ko" | "nl" | "pl" | "pt" | "pt-br" | "ru" | "tlh" | "tr" | "uk" | "uk-ua" | "zh" | "zh-cn" | "zh-tw" } SupportedLanguages
 */

/**
 * @typedef {Object} TurnstileOptions
 * @property {string} sitekey - Every widget has a sitekey. This sitekey is associated with the corresponding
 * widget configuration and is created upon widget creation in Cloudflare's dashboard.
 * This is the only required parameter.
 * - Data Attribute - `data-sitekey`
 * @property {string} [action] - A customer value that can be used to differentiate widgets under the same
 * sitekey in analytics and which is returned upon validation. This can only
 * contain up to 32 alphanumeric characters including `_` and `-`.
 * - Data Attribute - `data-action`
 * @property {string} [cData] - A customer payload that can be used to attach customer data to the challenge
 * throughout its issuance and which is returned upon validation. This can only
 * contain up to 255 alphanumeric characters including `_` and `-`.
 * - Data Attribute - `data-cdata`
 * @property {(token: string) => void} [callback] - Callback function invoked upon successful challenge completion.
 * - Data Attribute - `data-callback`
 * @property {(errorCode: string) => void} ["error-callback"] - Callback invoked when there is an error (e.g., network error, challenge failed).
 * - Data Attribute - `data-error-callback`
 * @property {"render" | "execute"} [execution="render"] - Execution controls when to obtain the token of the widget and can be on
 * `"render"` (default) or on `"execute"`.
 * - Data Attribute - `data-execution`
 * @property {() => void} ["expired-callback"] - Callback invoked when the token expires and does not reset the widget.
 * - Data Attribute - `data-expired-callback`
 * @property {() => void} ["before-interactive-callback"] - Callback invoked before the challenge enters interactive mode.
 * - Data Attribute - `data-before-interactive-callback`
 * @property {() => void} ["after-interactive-callback"] - Callback invoked when the challenge has left interactive mode.
 * - Data Attribute - `data-after-interactive-callback`
 * @property {() => void} ["unsupported-callback"] - Callback invoked when a given client/browser is not supported.
 * - Data Attribute - `data-unsupported-callback`
 * @property {() => void} ["timeout-callback"] - Callback invoked when the challenge expires.
 * - Data Attribute - `data-timeout-callback`
 * @property {"light" | "dark" | "auto"} [theme="auto"] - The widget theme. Can be `"light"`, `"dark"`, or `"auto"`.
 * - Data Attribute - `data-theme`
 * @property {SupportedLanguages | "auto" | string} [language] - Language to display, either `"auto"` or an ISO 639-1 two-letter language code.
 * - Data Attribute - `data-language`
 * @property {number} [tabindex=0] - The tabindex of Turnstile's iframe for accessibility purposes.
 * - Data Attribute - `data-tabindex`
 * @property {boolean} ["response-field"]=true - Controls if an input element with the response token is created.
 * - Data Attribute - `data-response-field`
 * @property {string} ["response-field-name"]="cf-turnstile-response" - Name of the input element.
 * - Data Attribute - `data-response-field-name`
 * @property {"normal" | "compact"} [size="normal"] - The widget size. Can be 'normal' or 'compact'.
 * - Data Attribute - `data-size`
 * @property {"auto" | "never"} [retry="auto"] - Automatically retry upon failure to obtain a token or never retry.
 * - Data Attribute - `data-retry`
 * @property {number} ["retry-interval"]=8000 - Time between retry attempts in milliseconds. Value must be between `0` and `900000`
 * (15 minutes). Only applies when `retry` is set to `auto`.
 * - Data Attribute - `data-retry-interval`
 * @property {"auto" | "manual" | "never"} ["refresh-expired"]="auto" - Controls the behavior when the token of a Turnstile widget has expired:
 * - Data Attribute - `data-refresh-expired`
 * @property {"always" | "execute" | "interaction-only"} [appearance="always"] - If a widget is visible, its appearance can be controlled via the `appearance` parameter:
 * - Data Attribute - `data-appearance`
 * @property {string} [chlPageData] - ENTERPRISE ONLY - undocumented
 */

/**
 * @typedef {Object} TurnstileObject
 * @property {(callback: () => any) => void} ready - If using synchronous loading, the callback will be called once the DOM is ready.
 * @property {() => void} implicitRender
 * @property {(container?: WidgetId | HTMLElement | ElementId, parameters?: TurnstileOptions) => void} execute - Executes the challenge after the render() function has been called with { ... execution: "execute" },
 * decoupling the appearance and rendering of a widget from its execution.
 * @property {(container: HTMLElement | ElementId, parameters?: TurnstileOptions) => string} render - Explicitly renders the Turnstile widget.
 * Passed parameters will be combined with `data-` attributes on the container.
 * Only the `sitekey` parameter is required.
 * @throws {Error} Will throw an error if any parameter is invalid.
 * @returns A `widgetId` string.
 * @property {(widget?: WidgetId | HTMLElement | ElementId) => void} reset - Resets the widget.
 * @property {(widget?: WidgetId | HTMLElement | ElementId) => void} remove - Removes the widget from the page.
 * @property {(widget?: WidgetId | HTMLElement | ElementId) => string} getResponse - Obtains the widget's response.
 * @property {(widget?: WidgetId | HTMLElement | ElementId) => boolean} isExpired - Checks if the widget has expired.
 */

/**
 * @typedef {string} WidgetId
 */

/**
 * @typedef {string} ElementId
 */

/**
 * Global namespace for the Turnstile library.
 * @type {any}
 */
const globalNamespace = window;

/**
 * State indicating the current state of Turnstile.
 * @type {string}
 */
let turnstileState =
  typeof globalNamespace.turnstile !== "undefined" ? "ready" : "unloaded";

/**
 * Function to ensure Turnstile is loaded.
 * @type {() => Promise<any>}
 */
let ensureTurnstile;

/**
 * Object responsible for loading the Turnstile API and ensuring it's only loaded once.
 * @type {{resolve: (value?: any) => void, reject: (reason?: any) => void}}
 */
let turnstileLoad;

const turnstileLoadPromise = new Promise((resolve, reject) => {
  turnstileLoad = { resolve, reject };
  if (turnstileState === "ready") resolve(undefined);
});

{
  const TURNSTILE_LOAD_FUNCTION = "cf__reactTurnstileOnLoad";
  const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

  ensureTurnstile = () => {
    if (turnstileState === "unloaded") {
      turnstileState = "loading";
      globalNamespace[TURNSTILE_LOAD_FUNCTION] = () => {
        turnstileLoad.resolve();
        turnstileState = "ready";
        delete globalNamespace[TURNSTILE_LOAD_FUNCTION];
      };
      const url = `${TURNSTILE_SRC}?onload=${TURNSTILE_LOAD_FUNCTION}&render=explicit`;
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.addEventListener("error", () => {
        turnstileLoad.reject("Failed to load Turnstile.");
        delete globalNamespace[TURNSTILE_LOAD_FUNCTION];
      });
      document.head.appendChild(script);
    }
    return turnstileLoadPromise;
  };
}

/**
 * Callback function invoked when Turnstile successfully verifies a token.
 * @callback VerifyCallback
 * @param {string} token - The verified token.
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Callback function invoked when an error occurs during Turnstile execution.
 * @callback ErrorCallback
 * @param {*} [error] - The error object or message.
 * @param {BoundTurnstileObject} [boundTurnstileObject] - The bound Turnstile object.
 */

/**
 * Callback function invoked when a Turnstile token expires.
 * @callback ExpireCallback
 * @param {string} token - The expired token.
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Callback function invoked when a Turnstile operation times out.
 * @callback TimeoutCallback
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Callback function invoked after Turnstile becomes interactive.
 * @callback AfterInteractiveCallback
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Callback function invoked before Turnstile becomes interactive.
 * @callback BeforeInteractiveCallback
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Callback function invoked when Turnstile is not supported.
 * @callback UnsupportedCallback
 * @param {BoundTurnstileObject} boundTurnstileObject - The bound Turnstile object.
 */

/**
 * Turnstile React component.
 * @param {TurnstileProps} props - The component props.
 * @returns {JSX.Element} Turnstile component.
 */
export default function Turnstile({
  id,
  className,
  style,
  sitekey,
  action,
  cData,
  theme = "auto",
  language,
  tabIndex,
  responseField,
  responseFieldName,
  size,
  fixedSize,
  retry,
  retryInterval,
  refreshExpired,
  appearance,
  execution,
  userRef,
  onVerify,
  onLoad,
  onError,
  onExpire,
  onTimeout,
  onAfterInteractive,
  onBeforeInteractive,
  onUnsupported,
}) {
  /**
   * @type {React.MutableRefObject<HTMLDivElement | null>}
   */
  const ownRef = useRef(null);
  /**
   * @type {TurnstileCallbacks}
   */
  const inplaceState = useState({
    onVerify,
    onLoad,
    onError,
    onExpire,
    onTimeout,
    onAfterInteractive,
    onBeforeInteractive,
    onUnsupported,
  })[0];

  const ref = userRef || ownRef;

  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;
    let widgetId = "";
    (async () => {
      // load turnstile
      if (turnstileState !== "ready") {
        try {
          await ensureTurnstile();
        } catch (e) {
          typeof inplaceState.onError === "function" && inplaceState.onError(e);
          return;
        }
      }
      if (cancelled || !ref.current) return;
      /**
       * @type {BoundTurnstileObject}
       */
      let boundTurnstileObject;
      /**
       * @type {TurnstileOptions}
       */
      const turnstileOptions = {
        sitekey,
        action,
        cData,
        theme,
        language,
        tabindex: tabIndex,
        "response-field": responseField,
        "response-field-name": responseFieldName,
        size,
        retry,
        "retry-interval": retryInterval,
        "refresh-expired": refreshExpired,
        appearance,
        execution,
        /**
         * @type {VerifyCallback}
         */
        callback: (token) =>
          typeof inplaceState.onVerify === "function" &&
          inplaceState.onVerify(token, boundTurnstileObject),
        /**
         * @type {ErrorCallback}
         */
        "error-callback": (error) =>
          typeof inplaceState.onError === "function" &&
          inplaceState.onError(error, boundTurnstileObject),
        /**
         * @type {ExpireCallback}
         */
        "expired-callback": (token) =>
          typeof inplaceState.onExpire === "function" &&
          inplaceState.onExpire(token, boundTurnstileObject),
        /**
         * @type {TimeoutCallback}
         */
        "timeout-callback": () =>
          typeof inplaceState.onTimeout === "function" &&
          inplaceState.onTimeout(boundTurnstileObject),
        /**
         * @type {AfterInteractiveCallback}
         */
        "after-interactive-callback": () =>
          typeof inplaceState.onAfterInteractive === "function" &&
          inplaceState.onAfterInteractive(boundTurnstileObject),
        /**
         * @type {BeforeInteractiveCallback}
         */
        "before-interactive-callback": () =>
          typeof inplaceState.onBeforeInteractive === "function" &&
          inplaceState.onBeforeInteractive(boundTurnstileObject),
        /**
         * @type {UnsupportedCallback}
         */
        "unsupported-callback": () =>
          typeof inplaceState.onUnsupported === "function" &&
          inplaceState.onUnsupported(boundTurnstileObject),
      };

      widgetId = window.turnstile.render(ref.current, turnstileOptions);
      boundTurnstileObject = createBoundTurnstileObject(widgetId);
      typeof inplaceState.onLoad === "function" &&
        inplaceState.onLoad(widgetId, boundTurnstileObject);
    })();
    return () => {
      cancelled = true;
      if (widgetId) window.turnstile.remove(widgetId);
    };
  }, [
    sitekey,
    action,
    cData,
    theme,
    language,
    tabIndex,
    responseField,
    responseFieldName,
    size,
    retry,
    retryInterval,
    refreshExpired,
    appearance,
    execution,
  ]);
  useEffect(() => {
    inplaceState.onVerify = onVerify;
    inplaceState.onLoad = onLoad;
    inplaceState.onError = onError;
    inplaceState.onExpire = onExpire;
    inplaceState.onTimeout = onTimeout;
    inplaceState.onAfterInteractive = onAfterInteractive;
    inplaceState.onBeforeInteractive = onBeforeInteractive;
    inplaceState.onUnsupported = onUnsupported;
  }, [
    onVerify,
    onLoad,
    onError,
    onExpire,
    onTimeout,
    onAfterInteractive,
    onBeforeInteractive,
    onUnsupported,
  ]);

  return (
    <div
      ref={ref}
      id={id}
      className={className}
      style={
        fixedSize
          ? {
              ...(style || {}),
              width: size === "compact" ? "130px" : "300px",
              height: size === "compact" ? "120px" : "65px",
            }
          : style
      }
    />
  );
}

/**
 * Props for the Turnstile component.
 * @typedef {Object} TurnstileProps
 * @property {string} sitekey - The sitekey for Turnstile.
 * @property {string} [action] - The action for Turnstile.
 * @property {string} [cData] - Additional data for Turnstile.
 * @property {"light" | "dark" | "auto"} [theme] - The theme for Turnstile.
 * @property {SupportedLanguages | "auto"} [language] - The language for Turnstile.
 * @property {number} [tabIndex] - The tab index for Turnstile.
 * @property {boolean} [responseField] - Whether to include a response field for Turnstile.
 * @property {string} [responseFieldName] - The name of the response field for Turnstile.
 * @property {"normal" | "invisible" | "compact"} [size] - The size of Turnstile.
 * @property {boolean} [fixedSize] - Whether Turnstile has a fixed size.
 * @property {"auto" | "never"} [retry] - Retry behavior for Turnstile.
 * @property {number} [retryInterval] - Retry interval for Turnstile.
 * @property {"auto" | "manual" | "never"} [refreshExpired] - Refresh behavior for Turnstile.
 * @property {"always" | "execute" | "interaction-only"} [appearance] - Appearance behavior for Turnstile.
 * @property {"render" | "execute"} [execution] - Execution behavior for Turnstile.
 * @property {string} [id] - The ID for Turnstile.
 * @property {React.MutableRefObject<HTMLDivElement>} [userRef] - Reference to the Turnstile element.
 * @property {string} [className] - The CSS class for Turnstile.
 * @property {React.CSSProperties} [style] - The inline style for Turnstile.
 *
 * @property {(token: string, boundTurnstile: BoundTurnstileObject) => void} [onVerify] - Callback when Turnstile verifies token.
 * @property {(widgetId: string, boundTurnstile: BoundTurnstileObject) => void} [onLoad] - Callback when Turnstile loads.
 * @property {(error?: Error | any, boundTurnstile?: BoundTurnstileObject) => void} [onError] - Callback when Turnstile encounters an error.
 * @property {(token: string, boundTurnstile: BoundTurnstileObject) => void} [onExpire] - Callback when Turnstile token expires.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onTimeout] - Callback when Turnstile times out.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onAfterInteractive] - Callback after Turnstile becomes interactive.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onBeforeInteractive] - Callback before Turnstile becomes interactive.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onUnsupported] - Callback when Turnstile is unsupported.
 */

/**
 * Callbacks for Turnstile.
 * @typedef {Object} TurnstileCallbacks
 * @property {(token: string, boundTurnstile: BoundTurnstileObject) => void} [onVerify] - Callback when Turnstile verifies token.
 * @property {(widgetId: string, boundTurnstile: BoundTurnstileObject) => void} [onLoad] - Callback when Turnstile loads.
 * @property {(error?: Error | any, boundTurnstile?: BoundTurnstileObject) => void} [onError] - Callback when Turnstile encounters an error.
 * @property {(token: string, boundTurnstile: BoundTurnstileObject) => void} [onExpire] - Callback when Turnstile token expires.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onTimeout] - Callback when Turnstile times out.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onAfterInteractive] - Callback after Turnstile becomes interactive.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onBeforeInteractive] - Callback before Turnstile becomes interactive.
 * @property {(boundTurnstile: BoundTurnstileObject) => void} [onUnsupported] - Callback when Turnstile is unsupported.
 */

/**
 * Bound Turnstile object.
 * @typedef {Object} BoundTurnstileObject
 * @property {(options?: TurnstileOptions) => void} execute - Executes Turnstile with options.
 * @property {() => void} reset - Resets Turnstile.
 * @property {() => void} getResponse - Gets Turnstile response.
 * @property {() => boolean} isExpired - Checks if Turnstile token is expired.
 */

/**
 * Creates a Bound Turnstile object.
 * @function createBoundTurnstileObject
 * @param {string} widgetId - The ID of the Turnstile widget.
 * @returns {BoundTurnstileObject} The Bound Turnstile object.
 */
function createBoundTurnstileObject(widgetId) {
  return {
    execute: (options) => window.turnstile.execute(widgetId, options),
    reset: () => window.turnstile.reset(widgetId),
    getResponse: () => window.turnstile.getResponse(widgetId),
    isExpired: () => window.turnstile.isExpired(widgetId),
  };
}

/**
 * Hook for accessing the Turnstile object.
 * @function useTurnstile
 * @returns {TurnstileObject} The Turnstile object.
 */
export function useTurnstile() {
  // we are using state here to trigger a component re-render once turnstile
  // loads, so the component using this hook gets the object once its loaded
  const [_, setState] = useState(turnstileState);

  useEffect(() => {
    if (turnstileState === "ready") return;
    turnstileLoadPromise.then(() => setState(turnstileState));
  }, []);

  return globalNamespace.turnstile;
}



// WEBPACK FOOTER //
// src/modules/theme/theme1/common/components/turnstile-react.jsx